<template>
  <template-card
    :template="template"
    :is_selected="is_selected"
    :preview_area_class="preview_area_class"
    @select="$emit('select', template)"
  >
    <template #preview>
      <widget-template-iframe
        :widget_config="preview_settings"
        :build_src="build_src"
        :send_settings="send_settings"
        :send_page_change="send_page_change"
        :send_autofill_test_data="send_autofill_test_data"
        :preview_mode="preview_mode"
        :current_page="current_page"
        :scale="scale"
        :width="card_width * width_multiplier"
        :height="height"
        disable_interactions
      />
    </template>
  </template-card>
</template>

<script>
import TemplateCard from '@/components/shared/cards/template-card.vue';
import WidgetTemplateIframe from '@/components/widget/widget-template-iframe.vue';

/**
 * Reusable template card that shows a widget preview.
 * Used in template galleries to display and select widget templates.
 *
 * @example
 * <widget-template-card
 *   :template="template"
 *   :is_selected="selected_template_id === template.id"
 *   :compose_preview_settings="composeGuestCheckInPreviewParams"
 *   :build_src="buildIframeSrc"
 *   :send_settings="sendSettings"
 *   preview_area_class="my-widget-preview-area"
 *   @select="onSelect"
 * />
 */
export default {
  name: 'WidgetTemplateCard',

  components: {
    TemplateCard,
    WidgetTemplateIframe,
  },

  props: {
    /**
     * Template object from backend
     */
    template: {
      type: Object,
      required: true,
    },

    /**
     * Whether this template is currently selected
     */
    is_selected: {
      type: Boolean,
      default: false,
    },

    /**
     * Function to compose preview settings from template + props
     * Signature: (props) => settings_object
     */
    compose_preview_settings: {
      type: Function,
      required: true,
    },

    /**
     * Function to build iframe src
     * Signature: (preview_mode, is_development) => string
     */
    build_src: {
      type: Function,
      required: true,
    },

    /**
     * Function to send settings to widget
     * Signature: (iframe, settings) => void
     */
    send_settings: {
      type: Function,
      required: true,
    },

    /**
     * Function to send autofill test data to widget (optional)
     * Signature: (iframe) => void
     */
    send_autofill_test_data: {
      type: Function,
      default: null,
    },

    /**
     * Function to send page change to widget (optional)
     * Signature: (iframe, page) => void
     */
    send_page_change: {
      type: Function,
      default: null,
    },

    /**
     * Widget-specific props to pass to compose_preview_settings
     * These will be merged with { template } and passed to the composer
     */
    widget_props: {
      type: Object,
      default: () => ({}),
    },

    /**
     * Preview mode for the widget iframe - e.g. 'mobile', 'tablet-landscape', etc.
     */
    preview_mode: {
      type: String,
      default: 'tablet-landscape',
    },

    /**
     * Current page to show in widget (if applicable)
     */
    current_page: {
      type: String,
      default: null,
    },

    /**
     * Scale factor for the preview
     */
    scale: {
      type: Number,
      default: 0.6,
    },

    /**
     * Width multiplier relative to card width
     */
    width_multiplier: {
      type: Number,
      default: 1.6,
    },

    /**
     * Custom height for preview (optional)
     */
    height: {
      type: [String, Number],
      default: null,
    },

    /**
     * Custom class for preview area styling. Alternatively, use a `.template-preview-area` deep selector
     */
    preview_area_class: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      card_width: 0,
    };
  },

  computed: {
    preview_settings() {
      const props_to_pass = {
        template: this.template,
        preview_mode: this.preview_mode,
        is_template_preview: true,
        ...this.widget_props,
      };

      return this.compose_preview_settings(props_to_pass);
    },
  },

  methods: {
    onResize() {
      this.card_width = this.$el?.clientWidth || 1200;
    },
  },

  mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
};
</script>

<style scoped lang="scss"></style>
